import { useAnonRestApi, useAuthRestApi } from '../common';
import { Observable } from 'rxjs';
import {
    ICountCollectionDto,
    IStudiesApi,
    IStudy,
    IStudyBulkUpdateSpecifier,
    IStudyBulkUploadSpecifier
} from '@vivli/features/studies/infrastructure/interface';
import { StudyStatusEnum } from '@vivli/features/studies/infrastructure/enum';
import { map } from 'rxjs/operators';
import { DataTransformationService } from '@vivli/shared/infrastructure/service';
import { AssignedAppTypeEnum } from '@vivli/shared/infrastructure/enum';
import { IDatasetUsageData } from '@vivli/features/datasets/infrastructure/interface';
import moment from 'moment';
import { IExternalLink, IUser } from '@vivli/shared/infrastructure/interface';

export const useStudiesApi = (): IStudiesApi => {
    const { handleGet, handlePost, handlePut, handleDelete } = useAuthRestApi();
    const anonRestApi = useAnonRestApi();

    const getAllStudies = (assignedAppType: AssignedAppTypeEnum): Observable<IStudy[]> => {
        const studyTypes = Object.keys(StudyStatusEnum).join(',');
        const appType: string = assignedAppType;
        return handleGet<IStudy[]>(`/studies/assignedAppType/${appType}/statuses/${studyTypes}`);
    };

    const getStudiesByStatus = (type: StudyStatusEnum, assignedAppType: AssignedAppTypeEnum): Observable<IStudy[]> => {
        const appType = assignedAppType;
        return handleGet<IStudy[]>(`/studies/assignedAppType/${appType}/statuses/${type}`).pipe(
            map(DataTransformationService.formatStudies)
        );
    };

    const getStudy = (studyId: string): Observable<IStudy> => {
        return handleGet<IStudy>(`/studies/${studyId}`);
    };

    const getStudyAnonymous = (studyId: string): Observable<IStudy> => {
        return anonRestApi.handleGet<IStudy>(`/studies/${studyId}/metadata`);
    };

    const getStudyFromDoiId = (studyId: string): Observable<IStudy> => {
        return anonRestApi.handleGet<IStudy>(`/studies/${studyId}/metadata/fromdoi`);
    };

    const getSampleStudy = (nctId: string): Observable<IStudy> => {
        return handleGet<IStudy>(`/studies/${nctId}/extract`);
    };

    const getStudyOwner = (userId: string): Observable<IUser> => {
        return handleGet<IUser>(`/users/${userId}/studyowner`);
    };

    const getSampleStudyForListingRequest = (nctId: string, studyId: string): Observable<IStudy> => {
        return handleGet<IStudy>(`/studies/${nctId}/extract/forListRequest/${studyId}`);
    };

    const getSampleStudyForNonNctListingRequest = (sponsorId: string, studyId: string): Observable<IStudy> => {
        return handleGet<IStudy>(`/studies/${sponsorId}/forListRequest/${studyId}`);
    };

    const getUsedIn = (studyId: string): Observable<string> => {
        return handleGet<string>(`/studies/${studyId}/usedIn`);
    };

    const getDoiForIpdDataPackage = (studyId: string, ipdDataPackageId: string): Observable<IStudy> => {
        return handleGet<IStudy>(`/studies/${ipdDataPackageId}/doi`);
    };

    const createStudyAndDataPackageDois = (studyId: string): Observable<string> => {
        return handlePost<string>(`/studies/${studyId}/dois`, null);
    };

    const updateStudy = (study: IStudy): Observable<IStudy> => {
        return handlePut<IStudy>(`/studies`, study);
    };

    const updateWithNewHistoryEntry = (study: IStudy): Observable<IStudy> => {
        return handlePut<IStudy>(`/studies/history`, study);
    };

    const setStudyStatus = (study: IStudy, status: StudyStatusEnum, comment: string): Observable<IStudy> => {
        return handlePut<IStudy>(`/studies/${study.id}/status/`, { status, comment });
    };

    const acceptJSONFromCochrane = (study: IStudy, comment: string): Observable<IStudy> => {
        return handlePut<IStudy>(`/studies/accept/`, { study, comment });
    };

    const setStudyIpdSubmittedStatus = (study: IStudy, isSubmitted: boolean): Observable<IStudy> => {
        return handlePut<IStudy>(`/studies/ipdsubmittedonly/${isSubmitted}`, study);
    };

    const addStudy = (study: IStudy, hasExtractedStudyData: boolean): Observable<IStudy> => {
        return handlePost<IStudy>(`/studies/${hasExtractedStudyData}`, study);
    };

    const bulkUploadStudies = (uploadSpec: IStudyBulkUploadSpecifier): Observable<void> => {
        return handlePost<void>(`/studies/bulkupload`, uploadSpec);
    };

    const bulkUpdateStudies = (updateSpec: IStudyBulkUpdateSpecifier): Observable<void> => {
        return handlePost<void>(`/studies/bulkupdate`, updateSpec);
    };

    const createIpdDataPackage = (studyId: string): Observable<IStudy> => {
        return handlePost<IStudy>(`/studies/${studyId}/ipdpackage`);
    };

    //DO NOT USE THIS METHOD for studies after posting! - deletes the study without any accompanying objects
    const deleteStudy = (studyId: string): Observable<void> => {
        return handleDelete<void>(`/studies/${studyId}`);
    };

    const deleteStudyIPD = (studyId: string, ipdDataPackageId: string): Observable<void> => {
        return handleDelete<void>(`/studies/${studyId}/${ipdDataPackageId}`);
    };

    const softDeleteStudyIPD = (studyId: string, ipdDataPackageId: string): Observable<void> => {
        return handleDelete<void>(`/studies/${studyId}/${ipdDataPackageId}/soft`);
    };

    //DataCount Methods
    const sendDataCountCollectionData = (studyDoi: string, fileDoi: string): Observable<string> => {
        const dto: ICountCollectionDto = {
            clientIp: '',
            userAgent: navigator.userAgent,
            studyDoi: studyDoi,
            fileDoi: fileDoi,
            referrer: studyDoi
        };
        return handlePut<string>('/datacount/collect', dto);
    };

    const getDataCountUsage = (studyId: string, postedDate: Date): Observable<IDatasetUsageData> => {
        return anonRestApi.handleGet<IDatasetUsageData>(`/datacount/usage?datasetId=${studyId}&postedDate=${moment(postedDate).format(`YYYY-MM-DD`)}`);
    };

    const updateExternalLinks = (studyId: string, externalLinks: IExternalLink[]): Observable<IStudy> => {
        return handlePut<IStudy>(`/studies/external-links/${studyId}`, externalLinks);
    };

    const getStudyExportData = (studyIds: { id: string; score: number }[]): Observable<string> => {
        return handlePost<string>(`/studies/searchStudiesExport`, studyIds);
    };

    return {
        getAllStudies,
        getStudiesByStatus,
        getStudy,
        getStudyAnonymous,
        getStudyFromDoiId,
        getSampleStudy,
        getStudyOwner,
        getSampleStudyForListingRequest,
        getSampleStudyForNonNctListingRequest,
        getUsedIn,
        getDoiForIpdDataPackage,
        createStudyAndDataPackageDois,
        updateStudy,
        updateWithNewHistoryEntry,
        setStudyStatus,
        acceptJSONFromCochrane,
        setStudyIpdSubmittedStatus,
        addStudy,
        bulkUploadStudies,
        bulkUpdateStudies,
        createIpdDataPackage,
        deleteStudy,
        deleteStudyIPD,
        softDeleteStudyIPD,
        sendDataCountCollectionData,
        getDataCountUsage,
        updateExternalLinks,
        getStudyExportData
    };
};
